import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useNotifications from '../hooks/useNotifications';
import LogoutModal from './LogoutModal';
import PaymentAlertsModal from './PaymentAlertsModal';
import BottomNavigation from './BottomNavigation';
import { 
  Home, 
  DollarSign, 
  Users, 
  Building2,
  Settings, 
  User, 
  LogOut,
  Bell
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, canAccessFinancial, canAccessSecretariat, canAccessAdministration, isPastor } = useAuth();
  const notifications = useNotifications(user);
  const { total: notifTotal, summary, fixedExpenses, hasFinancialPermission, isSecretaria } = notifications;
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useLayoutEffect(() => {
     // Atualiza variável CSS com a altura da barra inferior para aplicar um espaçamento acima
     const updateBottomNavHeight = () => {
       const el = document.querySelector('.bottom-navigation');
       const h = el ? el.offsetHeight : 0;
       document.documentElement.style.setProperty('--bottom-nav-height', `${h}px`);
       // offset adicional para que o conteúdo não fique sob a barra; pode ser ajustado
       document.documentElement.style.setProperty('--bottom-nav-bottom', '0px');
       // Removido gap extra para que o conteúdo toque a barra inferior
       document.documentElement.style.setProperty('--bottom-nav-gap', '0px');
       // incluir safe-area para iOS (Safari expõe via contantes CSS env())
       // não conseguimos ler env() via getComputedStyle, então usamos CSS calc com fallback
       // definimos apenas uma flag para indicar que devemos considerar safe-area
       document.documentElement.style.setProperty('--bottom-safe-area', 'var(env(safe-area-inset-bottom), 0px)');
     };
     updateBottomNavHeight();
     // Tentar novamente após o próximo frame caso a barra ainda não esteja montada
     const rafId = requestAnimationFrame(updateBottomNavHeight);
     // Observar mudanças de tamanho na barra
     const el = document.querySelector('.bottom-navigation');
     let resizeObserver;
     if (el && 'ResizeObserver' in window) {
       resizeObserver = new ResizeObserver(updateBottomNavHeight);
       resizeObserver.observe(el);
     }
     window.addEventListener('resize', updateBottomNavHeight);
     return () => {
       window.removeEventListener('resize', updateBottomNavHeight);
       cancelAnimationFrame(rafId);
       if (resizeObserver) resizeObserver.disconnect();
     };
   }, []);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Sidebar removida — menuItems/visibleMenuItems não são mais utilizados
  // const menuItems = useMemo(() => [], []);
  // const visibleMenuItems = useMemo(() => menuItems.filter(item => item.show), [menuItems]);

  // Função para mapear o pathname atual para o tab ativo da barra inferior
  const getActiveTab = (pathname) => {
    switch (pathname) {
      case '/home':
        return 'home';
      case '/financeiro':
        return 'financial';
      case '/celulas':
        return 'celulas';
      case '/secretaria':
        return 'secretary';
      case '/acolhimento':
        return 'acolhimento';
      case '/checkin':
        return 'checkin';
      case '/administracao':
        return 'admin';
      // Removido case '/whatsapp'
      case '/perfil':
        return 'profile';
      default:
        return 'home';
    }
  };

  // Função para lidar com mudanças de tab na barra inferior
  const handleTabChange = (tabId) => {
    switch (tabId) {
      case 'home':
        navigate('/home');
        break;
      case 'financial':
        if (canAccessFinancial()) {
          navigate('/financeiro');
        }
        break;
      case 'celulas':
        navigate('/celulas');
        break;
      case 'secretary':
        if (canAccessSecretariat()) {
          navigate('/secretaria');
        }
        break;
      case 'acolhimento':
        if (canAccessAdministration() || isPastor()) {
          navigate('/acolhimento');
        }
        break;
      case 'checkin':
        navigate('/checkin');
        break;
      case 'admin':
        if (canAccessAdministration()) {
          navigate('/administracao');
        }
        break;
      // Removido case 'whatsapp'
      case 'profile':
        navigate('/perfil');
        break;
      default:
        navigate('/home');
    }
  };

  const notifItemStyle = {
    display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
    padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
    borderBottom: '1px solid #f8fafc', textAlign: 'left',
    transition: 'background 0.15s',
  };
  const notifIconStyle = (bg) => ({
    width: '36px', height: '36px', borderRadius: '10px', background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '17px', flexShrink: 0,
  });
  const notifTitleStyle = { fontSize: '13px', fontWeight: '600', color: '#1e293b' };
  const notifDescStyle = { fontSize: '12px', color: '#64748b', marginTop: '1px' };
  const notifBadgeStyle = (color) => ({
    background: color, color: '#fff', borderRadius: '10px',
    padding: '2px 7px', fontSize: '11px', fontWeight: '700', flexShrink: 0,
  });

  return (
    <div className="flex flex-col h-screen bg-background-color">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)',
        padding: '0 20px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 12px rgba(30,58,138,0.08)',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/apas.png" alt="Apascentar" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />
          <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e3a8a', letterSpacing: '0.1px' }}>
            Apascentar
          </div>
        </div>

        {/* Notificações */}
        {(hasFinancialPermission || isSecretaria) && (
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotifications(v => !v)}
              title="Notificações"
              style={{
                width: '40px', height: '40px', padding: '0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: showNotifications ? 'rgba(30,58,138,0.12)' : 'rgba(30,58,138,0.06)',
                border: '1px solid rgba(30,58,138,0.12)',
                borderRadius: '10px', cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Bell size={18} color="#1e3a8a" strokeWidth={2} />
              {notifTotal > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  background: '#ef4444', color: '#fff',
                  borderRadius: '10px', padding: '1px 5px',
                  fontSize: '10px', fontWeight: '700', lineHeight: '14px',
                  minWidth: '16px', textAlign: 'center',
                }}>
                  {notifTotal > 99 ? '99+' : notifTotal}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {showNotifications && (
              <div style={{
                position: 'absolute', top: '48px', right: 0,
                background: '#ffffff', borderRadius: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                border: '1px solid #e2e8f0',
                width: '300px', zIndex: 999, overflow: 'hidden',
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>Notificações</span>
                </div>

                {notifTotal === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    ✓ Tudo em dia
                  </div>
                ) : (
                  <div>
                    {isSecretaria && (summary.pedidosOracao || 0) > 0 && (
                      <button onClick={() => { navigate('/acolhimento'); setShowNotifications(false); }}
                        style={notifItemStyle}>
                        <span style={notifIconStyle('#fef3c7')}>🙏</span>
                        <div style={{ flex: 1 }}>
                          <div style={notifTitleStyle}>Pedidos de Oração</div>
                          <div style={notifDescStyle}>{summary.pedidosOracao} pedido{summary.pedidosOracao !== 1 ? 's' : ''} aguardando</div>
                        </div>
                        <span style={notifBadgeStyle('#f59e0b')}>{summary.pedidosOracao}</span>
                      </button>
                    )}
                    {isSecretaria && (summary.preCadastros || 0) > 0 && (
                      <button onClick={() => { navigate('/secretaria'); setShowNotifications(false); }}
                        style={notifItemStyle}>
                        <span style={notifIconStyle('#ede9fe')}>👤</span>
                        <div style={{ flex: 1 }}>
                          <div style={notifTitleStyle}>Pré-cadastros</div>
                          <div style={notifDescStyle}>{summary.preCadastros} pendente{summary.preCadastros !== 1 ? 's' : ''}</div>
                        </div>
                        <span style={notifBadgeStyle('#8b5cf6')}>{summary.preCadastros}</span>
                      </button>
                    )}
                    {isSecretaria && (summary.novosUsuarios || 0) > 0 && (
                      <button onClick={() => { navigate('/secretaria'); setShowNotifications(false); }}
                        style={notifItemStyle}>
                        <span style={notifIconStyle('#d1fae5')}>🆕</span>
                        <div style={{ flex: 1 }}>
                          <div style={notifTitleStyle}>Novos Membros</div>
                          <div style={notifDescStyle}>{summary.novosUsuarios} novo{summary.novosUsuarios !== 1 ? 's' : ''} esta semana</div>
                        </div>
                        <span style={notifBadgeStyle('#10b981')}>{summary.novosUsuarios}</span>
                      </button>
                    )}
                    {hasFinancialPermission && (notifications.summary?.alertasFinanceiros || 0) > 0 || (hasFinancialPermission && fixedExpenses?.length > 0 && notifTotal > 0) ? (
                      <button onClick={() => { setShowAlertsModal(true); setShowNotifications(false); }}
                        style={notifItemStyle}>
                        <span style={notifIconStyle('#fee2e2')}>💰</span>
                        <div style={{ flex: 1 }}>
                          <div style={notifTitleStyle}>Alertas Financeiros</div>
                          <div style={notifDescStyle}>Despesas a vencer</div>
                        </div>
                        <span style={notifBadgeStyle('#ef4444')}>{notifications.paymentAlerts?.alertCount || '!'}</span>
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar e conteúdo principal */}
      <div className="flex flex-1 overflow-hidden">
        {/* Conteúdo principal (sidebar removida) */}
        <div className="flex-1 app-main-scroll">
          <div className="app-container">
            {children}
          </div>
        </div>
      </div>

      {/* Barra de navegação inferior */}
      <BottomNavigation 
        activeTab={getActiveTab(location.pathname)}
        onTabChange={handleTabChange}
        onLogout={handleLogout}
      />

      {/* Modals */}
      {showLogoutModal && (
        <LogoutModal 
          isOpen={showLogoutModal}
          onClose={cancelLogout}
          onConfirm={confirmLogout}
        />
      )}

      {showAlertsModal && (
        <PaymentAlertsModal 
          isOpen={true}
          onClose={() => setShowAlertsModal(false)}
          fixedExpenses={fixedExpenses}
        />
      )}
    </div>
  );
};

export default Layout;
